import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DialogDescription } from "@radix-ui/react-dialog";
import { ShieldCheck, Gavel, Scale, AlertTriangle, UserCheck } from "lucide-react";
import { useMustKnowLaws } from "@/hooks/use-supabase-data";
import { Skeleton } from "@/components/ui/skeleton";

interface LawPoint {
  title: string;
  points: string[];
}

interface Law {
  id: string;
  title: string;
  summary: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  rights: LawPoint;
  punishment: LawPoint;
  support: LawPoint;
  tip?: string | null;
  order?: number;
}

const iconCatalog = {
  "shield-check": ShieldCheck,
  gavel: Gavel,
  scale: Scale,
  "alert-triangle": AlertTriangle,
  "user-check": UserCheck,
} satisfies Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>;

const fallbackLaws: Law[] = [
  {
    id: "no-police-beating",
    title: "Police Cannot Physically Harm You",
    summary: "Know what force police can use and how to report misconduct.",
    icon: ShieldCheck,
    rights: {
      title: "Your Rights",
      points: [
        "You have the right to be treated with dignity during any investigation or arrest.",
        "You can insist on a medical examination if you are harmed or threatened.",
        "You may request to have a friend, family member, or lawyer informed of your detention.",
      ],
    },
    punishment: {
      title: "What Happens If Police Use Excessive Force",
      points: [
        "Police officers can be suspended and face departmental inquiries.",
        "Serious injuries caused by police can lead to criminal charges against the officers involved.",
        "Courts can award compensation to the victim for unlawful police actions.",
      ],
    },
    support: {
      title: "If You Need Help",
      points: [
        "Call the National Human Rights Commission (NHRC) helpline: 14433.",
        "File a written complaint with the Superintendent of Police (SP) or District Collector.",
        "Seek legal assistance through District Legal Services Authority (free legal aid).",
      ],
    },
    tip:
      "If you experience police violence, immediately seek medical attention and document injuries with photos and doctor reports.",
  },
  {
    id: "electricity-notice",
    title: "Electricity Supply Cannot Be Cut Without Notice",
    summary: "Understand the steps your electricity provider must follow.",
    icon: Gavel,
    rights: {
      title: "Your Rights",
      points: [
        "You must receive a written notice mentioning the outstanding amount and due date.",
        "You are entitled to a minimum 7-day notice period before any disconnection.",
        "Senior citizens and differently abled persons can request special consideration.",
      ],
    },
    punishment: {
      title: "If Disconnected Without Notice",
      points: [
        "You can register a complaint with the Consumer Grievance Redressal Forum of the power company.",
        "The provider may have to reconnect supply immediately and pay a penalty.",
        "You can escalate to the state electricity ombudsman or consumer court for compensation.",
      ],
    },
    support: {
      title: "If You Need Help",
      points: [
        "Call the electricity board customer care or visit the nearest office with your consumer number.",
        "Approach the consumer helpline (National Toll Free: 1800-11-4000, 14404).",
        "Keep copies of bills, notices, and payment receipts as evidence.",
      ],
    },
  },
  {
    id: "arrest-rights",
    title: "Know Your Arrest Rights",
    summary: "Simple steps to follow if you or someone you know is arrested.",
    icon: UserCheck,
    rights: {
      title: "Your Rights",
      points: [
        "Police must disclose the grounds of arrest and show their ID if in plain clothes.",
        "You have the right to remain silent and to speak to a lawyer of your choice.",
        "Women cannot be arrested after sunset or before sunrise without special permission from a magistrate.",
      ],
    },
    punishment: {
      title: "If Procedure Is Not Followed",
      points: [
        "Illegal arrest can lead to immediate release by a magistrate.",
        "Police officers can face departmental action or legal penalties.",
        "You may be entitled to compensation from the courts.",
      ],
    },
    support: {
      title: "If You Need Help",
      points: [
        "Call a legal aid helpline or District Legal Services Authority for a free lawyer.",
        "Inform a trusted person; police must note the time and person notified.",
        "Request a copy of the arrest memo and ensure it is countersigned by a witness.",
      ],
    },
  },
  {
    id: "consumer-rights",
    title: "Consumer Rights Against Cheating",
    summary: "How to act if a shopkeeper cheats with weight, price, or quality.",
    icon: Scale,
    rights: {
      title: "Your Rights",
      points: [
        "You should receive a proper bill or receipt for every purchase.",
        "You can return defective goods within the specified warranty period.",
        "Weights and measures must carry the Legal Metrology seal; you can check for tampering.",
      ],
    },
    punishment: {
      title: "If Seller Cheats You",
      points: [
        "Complaints can lead to fines, cancellation of license, or even jail for repeated offences.",
        "Consumer courts can order refunds, replacement, or compensation for harassment.",
        "Legal Metrology officials can seize tampered equipment and levy penalties.",
      ],
    },
    support: {
      title: "If You Need Help",
      points: [
        "Use the National Consumer Helpline (1800-11-4000) or file a complaint on consumerhelpline.gov.in.",
        "Visit the local consumer court with bills, product details, and written complaint.",
        "Talk to a consumer rights NGO for guidance on drafting complaints.",
      ],
    },
  },
  {
    id: "landlord-tenant",
    title: "Landlord-Tenant Safety Rules",
    summary: "Basic protections if you are renting a home or room.",
    icon: AlertTriangle,
    rights: {
      title: "Your Rights",
      points: [
        "Landlords must give reasonable written notice before asking tenants to vacate.",
        "Security deposit deductions should be clearly itemized with reasons.",
        "Repairs affecting basic living conditions must be addressed promptly.",
      ],
    },
    punishment: {
      title: "If Landlord Harasses You",
      points: [
        "Illegal eviction without notice can be challenged in rent control court or police station.",
        "Landlords may be fined for cutting utilities or locking tenants out.",
        "You can claim damages for losses caused by unlawful eviction or harassment.",
      ],
    },
    support: {
      title: "If You Need Help",
      points: [
        "Collect rent receipts, lease agreements, and communication records.",
        "Approach the local Rent Control Authority or civil court.",
        "Seek legal aid or tenant associations for mediation and advice.",
      ],
    },
  },
];

export function MustKnowLawsSection() {
  const { laws: remoteLaws, loading } = useMustKnowLaws();
  const [selectedLaw, setSelectedLaw] = useState<Law | null>(null);

  const lawData = useMemo(() => {
    if (remoteLaws.length > 0) {
      return remoteLaws.map((law, index) => {
        const Icon = iconCatalog[law.icon] ?? ShieldCheck;
        const parsePoints = (points: string[] | null | undefined) =>
          Array.isArray(points) ? points.filter((point) => point && point.trim().length > 0) : [];

        return {
          id: law.id,
          title: law.title,
          summary: law.summary,
          icon: Icon,
          rights: {
            title: law.rights_title || "Your Rights",
            points: parsePoints(law.rights_points),
          },
          punishment: {
            title: law.punishment_title || "If The Law Is Broken",
            points: parsePoints(law.punishment_points),
          },
          support: {
            title: law.support_title || "If You Need Help",
            points: parsePoints(law.support_points),
          },
          tip: law.tip,
          order: law.display_order ?? index,
        } as Law;
      });
    }
    return fallbackLaws;
  }, [remoteLaws]);

  const sortedLaws = useMemo(
    () =>
      lawData
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((law) => {
          const { order, ...rest } = law;
          return rest;
        }),
    [lawData],
  );

  const lawCards = useMemo(
    () =>
      sortedLaws.map((law) => (
        <button
          key={law.id}
          type="button"
          onClick={() => setSelectedLaw(law)}
          className="p-6 text-left rounded-2xl bg-card neo-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-background neo-icon-box flex-shrink-0">
              <law.icon className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h3 className="font-poppins text-lg font-semibold text-foreground mb-1">
                {law.title}
              </h3>
              <p className="font-inter text-sm text-muted-foreground">{law.summary}</p>
            </div>
          </div>
        </button>
      )),
    [sortedLaws],
  );

  return (
    <section className="py-12" data-testid="section-must-know-laws">
      <div className="text-center mb-8 space-y-3">
        <h2 className="font-poppins text-3xl md:text-4xl font-bold text-foreground">
          Must Know Laws
        </h2>
        <p className="font-inter text-muted-foreground text-lg max-w-2xl mx-auto">
          Everyday protections everyone should know. Tap on a law to learn your rights,
          possible penalties, and where to seek quick help.
        </p>
      </div>

      {loading && remoteLaws.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="p-6 rounded-2xl bg-card neo-card space-y-4">
              <div className="flex items-start gap-4">
                <Skeleton className="w-12 h-12 rounded-xl neo-icon-box" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-6 w-40 rounded-xl neo-card-sm" />
                  <Skeleton className="h-4 w-56 rounded-xl neo-card-sm" />
                </div>
              </div>
              <Skeleton className="h-4 w-4/5 rounded-xl neo-card-sm" />
              <Skeleton className="h-4 w-2/3 rounded-xl neo-card-sm" />
            </div>
          ))}
        </div>
      ) : sortedLaws.length === 0 ? (
        <div className="rounded-2xl bg-muted/40 border border-dashed border-muted-foreground/30 p-6 text-center text-muted-foreground">
          No laws have been added yet. Check back soon.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{lawCards}</div>
      )}

      <Dialog open={Boolean(selectedLaw)} onOpenChange={(open) => !open && setSelectedLaw(null)}>
        <DialogContent className="w-[94vw] max-w-2xl rounded-2xl p-4 sm:p-6">
          {selectedLaw && (
            <div className="max-h-[70vh] overflow-y-auto pr-1">
              <DialogHeader className="space-y-2 text-left sticky top-0 bg-background pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-primary/10 text-primary">
                    <selectedLaw.icon className="w-6 h-6" />
                  </div>
                  <DialogTitle className="text-2xl font-semibold">{selectedLaw.title}</DialogTitle>
                </div>
                <DialogDescription className="text-base text-muted-foreground leading-relaxed">
                  {selectedLaw.summary}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 pt-4">
                {[selectedLaw.rights, selectedLaw.punishment, selectedLaw.support].map((block) => (
                  <div key={block.title} className="rounded-xl bg-muted/40 p-4 sm:p-5">
                    <h3 className="font-poppins text-lg font-semibold text-foreground mb-3">
                      {block.title}
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
                      {block.points.map((point, index) => (
                        <li key={index}>{point}</li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4">
                  <p className="text-sm text-muted-foreground">
                    <strong>Tip:</strong>{" "}
                    {selectedLaw.tip ||
                      "Keep copies of all documents, note names of officials you interact with, and always ask for written acknowledgement of your complaints."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

